/**
 * <pf-function-split>
 *
 * Switching for the Product Functions split block. The server already renders
 * the finished tab structure (list of role="tab" rows, one role="tabpanel" per
 * function, the first one active), so this only wires up clicks and the
 * keyboard pattern of a vertical tablist.
 *
 * Panels are toggled through data-active rather than the hidden attribute:
 * on desktop the inactive panels must keep their space (visibility: hidden in
 * the CSS) so the card stays as tall as its longest function.
 */
class PfFunctionSplit extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (this.tabs.length === 0 || this.tabs.length !== this.panels.length) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;
    const list = this.querySelector('[role="tablist"]');

    list.addEventListener('click', this.onClick.bind(this), { signal });
    list.addEventListener('keydown', this.onKeydown.bind(this), { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  onClick(event) {
    const tab = event.target.closest('[role="tab"]');
    if (!tab) return;

    this.select(this.tabs.indexOf(tab));
  }

  onKeydown(event) {
    const current = this.tabs.indexOf(event.target.closest('[role="tab"]'));
    if (current === -1) return;

    const last = this.tabs.length - 1;
    let next = null;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = current === last ? 0 : current + 1;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = current === 0 ? last : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next, { focus: true });
  }

  select(index, { focus = false } = {}) {
    if (index < 0 || index >= this.tabs.length) return;

    this.tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      this.panels[i].dataset.active = isActive ? 'true' : 'false';
    });

    if (focus) this.tabs[index].focus();
  }
}

if (!customElements.get('pf-function-split')) {
  customElements.define('pf-function-split', PfFunctionSplit);
}
